import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getToken } from '../utils/auth';
import './FieldSettingsPage.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Field definitions per certificate type
const SLC_FIELDS = [
  { id: 1, label: 'Certificate Number' },
  { id: 2, label: 'Book Number' },
  { id: 3, label: 'Student Name', autoFetch: true },
  { id: 4, label: "Father's Name", autoFetch: true },
  { id: 5, label: "Mother's Name", autoFetch: true },
  { id: 6, label: 'Guardian Name' },
  { id: 7, label: 'Date of Birth', autoFetch: true },
  { id: 8, label: 'Date of Birth (in Words)' },
  { id: 9, label: 'Gender', autoFetch: true },
  { id: 10, label: 'Nationality' },
  { id: 11, label: 'Religion' },
  { id: 12, label: 'Category' },
  { id: 13, label: 'Admission Number', autoFetch: true },
  { id: 14, label: 'Date of First Admission' },
  { id: 15, label: 'Class First Admitted' },
  { id: 16, label: 'Class Last Studied', autoFetch: true },
  { id: 17, label: 'Section Last Studied', autoFetch: true },
  { id: 18, label: 'Subjects Studied' },
  { id: 19, label: 'Qualified for Promotion' },
  { id: 20, label: 'Exam Result' },
  { id: 21, label: 'Total Working Days' },
  { id: 22, label: 'Total Days Attended' },
  { id: 23, label: 'Conduct' },
  { id: 24, label: 'Date of Leaving' },
  { id: 25, label: 'Reason for Leaving' },
  { id: 26, label: 'All Dues Cleared' },
  { id: 27, label: 'Extra-curricular Activities' },
  { id: 28, label: 'Remarks' },
  { id: 29, label: 'Certificate Date' },
];

const TC_FIELDS = [
  { id: 1, label: 'Certificate Number' },
  { id: 2, label: 'Book Number' },
  { id: 3, label: 'Admission Number', autoFetch: true },
  { id: 4, label: 'Student Name', autoFetch: true },
  { id: 5, label: "Father's Name", autoFetch: true },
  { id: 6, label: "Mother's Name", autoFetch: true },
  { id: 7, label: 'Guardian Name' },
  { id: 8, label: 'Date of Birth', autoFetch: true },
  { id: 9, label: 'Date of Birth (in Words)' },
  { id: 10, label: 'Gender', autoFetch: true },
  { id: 11, label: 'Nationality' },
  { id: 12, label: 'Religion' },
  { id: 13, label: 'Caste' },
  { id: 14, label: 'Category' },
  { id: 15, label: 'Aadhaar Number' },
  { id: 43, label: 'PEN (Permanent Education Number)' },
  { id: 16, label: 'Address', autoFetch: true },
  { id: 17, label: 'Date of First Admission' },
  { id: 18, label: 'Class First Admitted' },
  { id: 19, label: 'Class Last Studied', autoFetch: true },
  { id: 20, label: 'Section Last Studied', autoFetch: true },
  { id: 21, label: 'Academic Session' },
  { id: 22, label: 'School Board' },
  { id: 23, label: 'Medium of Instruction' },
  { id: 24, label: 'Subjects Studied' },
  { id: 25, label: 'Qualified for Promotion' },
  { id: 26, label: 'Exam Result' },
  { id: 27, label: 'Total Working Days' },
  { id: 28, label: 'Total Days Attended' },
  { id: 29, label: 'NCC / Scout & Guide' },
  { id: 30, label: 'Extra-curricular Activities' },
  { id: 31, label: 'Conduct' },
  { id: 32, label: 'Date of Leaving' },
  { id: 33, label: 'Reason for Leaving' },
  { id: 34, label: 'All Dues Cleared' },
  { id: 35, label: 'TC Issue Date' },
  { id: 36, label: 'Is Disabled' },
  { id: 37, label: 'Disability Details' },
  { id: 38, label: 'Games Played' },
  { id: 39, label: 'Height at Leaving' },
  { id: 40, label: 'Weight at Leaving' },
  { id: 41, label: 'Remarks' },
  { id: 42, label: 'Certificate Date' },
];

const CHAR_FIELDS = [
  { id: 1, label: 'Certificate Number' },
  { id: 2, label: 'Student Name', autoFetch: true },
  { id: 3, label: "Father's Name", autoFetch: true },
  { id: 4, label: "Mother's Name", autoFetch: true },
  { id: 5, label: 'Class', autoFetch: true },
  { id: 6, label: 'Section', autoFetch: true },
  { id: 7, label: 'Academic Session' },
  { id: 8, label: 'Character Rating' },
  { id: 9, label: 'Remarks' },
  { id: 10, label: 'Certificate Date' },
];

const BONAFIDE_FIELDS = [
  { id: 1, label: 'Certificate Number' },
  { id: 2, label: 'Student Name', autoFetch: true },
  { id: 3, label: "Father's Name", autoFetch: true },
  { id: 4, label: "Mother's Name", autoFetch: true },
  { id: 5, label: 'Class', autoFetch: true },
  { id: 6, label: 'Section', autoFetch: true },
  { id: 7, label: 'Admission Number', autoFetch: true },
  { id: 8, label: 'Academic Session' },
  { id: 9, label: 'Purpose' },
  { id: 10, label: 'Remarks' },
  { id: 11, label: 'Certificate Date' },
];

const SLC_DEFAULT_FIELDS = [3, 4, 5, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 28, 29];
const TC_DEFAULT_FIELDS = [3, 4, 5, 6, 8, 9, 10, 11, 12, 13, 14, 16, 17, 18, 19, 20, 21, 24, 25, 26, 27, 28, 31, 32, 33, 34, 35, 41, 42, 43];
const CHAR_DEFAULT_FIELDS = [2, 3, 4, 5, 6, 7, 8, 9, 10];
const BONAFIDE_DEFAULT_FIELDS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const TABS = [
  { key: 'SLC', label: 'SLC', fields: SLC_FIELDS, defaults: SLC_DEFAULT_FIELDS },
  { key: 'TC', label: 'Transfer Certificate', fields: TC_FIELDS, defaults: TC_DEFAULT_FIELDS },
  { key: 'CHARACTER', label: 'Character', fields: CHAR_FIELDS, defaults: CHAR_DEFAULT_FIELDS },
  { key: 'BONAFIDE', label: 'Bonafide', fields: BONAFIDE_FIELDS, defaults: BONAFIDE_DEFAULT_FIELDS },
];

const FieldSettingsPage = () => {
  const { schoolId } = useParams();
  const [activeTab, setActiveTab] = useState('SLC');
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const token = getToken(schoolId);

  const fetchConfig = useCallback(async (docType) => {
    try {
      const res = await fetch(`${API}/field-config/${docType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.data) {
        setConfigs(prev => ({ ...prev, [docType]: data.data }));
      }
    } catch (err) {
      console.error('Failed to fetch field config', err);
    }
  }, [token]);

  useEffect(() => {
    TABS.forEach(tab => fetchConfig(tab.key));
  }, [fetchConfig]);

  const getEnabledFields = (docType) => {
    const tab = TABS.find(t => t.key === docType);
    if (configs[docType]) return configs[docType];
    return tab ? tab.defaults : [];
  };

  const toggleField = (docType, fieldId) => {
    const current = getEnabledFields(docType);
    const updated = current.includes(fieldId)
      ? current.filter(id => id !== fieldId)
      : [...current, fieldId];
    setConfigs(prev => ({ ...prev, [docType]: updated }));
  };

  const selectAll = (docType) => {
    const tab = TABS.find(t => t.key === docType);
    const allIds = tab.fields.map(f => f.id);
    setConfigs(prev => ({ ...prev, [docType]: allIds }));
  };

  const deselectAll = (docType) => {
    setConfigs(prev => ({ ...prev, [docType]: [] }));
  };

  const resetDefaults = (docType) => {
    const tab = TABS.find(t => t.key === docType);
    setConfigs(prev => ({ ...prev, [docType]: [...tab.defaults] }));
  };

  const handleSave = async (docType) => {
    const fields = getEnabledFields(docType);
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const res = await fetch(`${API}/field-config/${docType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fields }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ text: `${docType} field settings saved successfully`, type: 'success' });
      } else {
        setMessage({ text: data.message || 'Save failed', type: 'error' });
      }
    } catch {
      setMessage({ text: 'Network error', type: 'error' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  const currentTab = TABS.find(t => t.key === activeTab);
  const enabledFields = getEnabledFields(activeTab);

  return (
    <div className="fieldsettings-page">
      <div className="fieldsettings-header">
        <h1 className="page-title">Document Field Settings</h1>
        <p className="fieldsettings-subtitle">Configure which fields appear on each certificate type</p>
      </div>

      {message.text && (
        <div className={`docgen-alert docgen-alert-${message.type}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage({ text: '', type: '' })}>&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="fieldsettings-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`fieldsettings-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className="fieldsettings-tab-count">
              {getEnabledFields(tab.key).length}/{tab.fields.length}
            </span>
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="fieldsettings-actions">
        <button className="docgen-btn-sm" onClick={() => selectAll(activeTab)}>Select All</button>
        <button className="docgen-btn-sm" onClick={() => deselectAll(activeTab)}>Deselect All</button>
        <button className="docgen-btn-sm" onClick={() => resetDefaults(activeTab)}>Reset Defaults</button>
        <div className="fieldsettings-actions-spacer" />
        <button
          className="docgen-btn docgen-btn-primary"
          onClick={() => handleSave(activeTab)}
          disabled={saving}
        >
          {saving ? 'Saving...' : `Save ${currentTab.label} Settings`}
        </button>
      </div>

      {/* Field Grid */}
      <div className="fieldsettings-grid">
        {currentTab.fields.map(field => (
          <label key={field.id} className={`fieldsettings-field ${enabledFields.includes(field.id) ? 'checked' : ''}`}>
            <input
              type="checkbox"
              checked={enabledFields.includes(field.id)}
              onChange={() => toggleField(activeTab, field.id)}
            />
            <span className="fieldsettings-field-label">{field.label}</span>
            {field.autoFetch && <span className="fieldsettings-auto-tag">Auto</span>}
          </label>
        ))}
      </div>
    </div>
  );
};

export default FieldSettingsPage;
